import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationItem } from '../models/notification.model';
import { AuthenticationService } from './Authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly API_URL = `${environment.apiUrl}/notifications`;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);

  readonly notifications = signal<NotificationItem[]>([]);

  readonly unreadCount = computed(() => {
    return this.notifications().filter((n) => !n.read).length;
  });

  constructor() {
    if (this.authService.getAccessToken()) {
      this.loadNotifications().subscribe();
    }
  }

  loadNotifications(): Observable<NotificationItem[]> {
    if (!this.authService.getAccessToken()) {
      return of([]);
    }

    return this.http.get<any>(this.API_URL).pipe(
      map((res) => {
        let rawItems: any[] = [];

        if (res?.data?.notifications && Array.isArray(res.data.notifications)) {
          rawItems = res.data.notifications;
        } else if (res?.notifications && Array.isArray(res.notifications)) {
          rawItems = res.notifications;
        } else if (res?.data?.items && Array.isArray(res.data.items)) {
          rawItems = res.data.items;
        } else if (res?.items && Array.isArray(res.items)) {
          rawItems = res.items;
        } else if (res?.data && Array.isArray(res.data)) {
          rawItems = res.data;
        } else if (Array.isArray(res)) {
          rawItems = res;
        }

        const currentUser = this.authService.currentUser();
        if (currentUser?.username === 'guest' || currentUser?.name === 'Guest User' || currentUser?.id === '1') {
          if (!rawItems.some((n: any) => n.title?.includes('Guest Mode'))) {
            rawItems.unshift({
              id: 'guest-welcome',
              title: 'Guest Mode Active',
              message: 'You are currently browsing the Support Desk as a Guest User.',
              createdAt: new Date().toISOString(),
              isRead: false,
              type: 'system',
            });
          }
        }

        return rawItems.map((n: any) => {
          let ticketId: string | undefined = undefined;
          if (n.linkUrl) {
            const match = String(n.linkUrl).match(/(\d+)/);
            if (match) ticketId = match[1];
          } else if (n.ticketId) {
            ticketId = String(n.ticketId);
          }

          return {
            id: String(n.id),
            title: n.title,
            message: n.message,
            ticketId: ticketId,
            ticketNumber: n.title && n.title.includes('#') ? n.title.substring(n.title.indexOf('#')) : 'TIC',
            timestamp: n.createdAt ? new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now',
            read: !!n.isRead,
            type: (n.type === 'assigned' || n.type === 'comment' || n.type === 'mentioned' || n.type === 'status_change' || n.type === 'system' ? n.type : 'system'),
          };
        });
      }),
      tap((mapped: NotificationItem[]) => {
        if (Array.isArray(mapped)) {
          this.notifications.set(mapped);
        }
      }),
      catchError(() => of([])),
    );
  }

  addNotification(data: { title: string; message: string; type?: NotificationItem['type']; ticketId?: string | number }): void {
    const item: NotificationItem = {
      id: String(Date.now()),
      title: data.title,
      message: data.message,
      ticketId: data.ticketId ? String(data.ticketId) : undefined,
      ticketNumber: data.ticketId ? `TIC-${data.ticketId}` : 'TIC',
      timestamp: 'Just now',
      read: false,
      type: data.type ?? 'system',
    };

    this.notifications.update((items) => [item, ...items]);
  }

  addLiveNotification(data: any): void {
    const notif = data?.notification || data;
    if (!notif) return;

    let ticketId: string | undefined = undefined;
    const linkStr = notif.linkUrl || data?.linkUrl;
    if (linkStr) {
      const match = String(linkStr).match(/(\d+)/);
      if (match) ticketId = match[1];
    } else if (data?.ticketId) {
      ticketId = String(data.ticketId);
    }

    const newItem: NotificationItem = {
      id: String(notif.id || Date.now()),
      title: notif.title || data?.title || 'Ticket Notification',
      message: notif.message || data?.message || 'New update received',
      ticketId: ticketId ? String(ticketId) : undefined,
      ticketNumber: notif.title && notif.title.includes('#') ? notif.title.substring(notif.title.indexOf('#')) : (data?.ticketNumber || 'TIC'),
      timestamp: 'Just now',
      read: false,
      type: (notif.type === 'assigned' || notif.type === 'comment' || notif.type === 'mentioned' || notif.type === 'status_change' || notif.type === 'system' ? notif.type : 'system'),
    };

    this.notifications.update((items) => [newItem, ...items.filter((i) => String(i.id) !== String(newItem.id))]);
  }

  markAsRead(id: string): void {
    this.notifications.update((items) =>
      items.map((n) => (String(n.id) === String(id) ? { ...n, read: true } : n))
    );

    if (id && !id.startsWith('17')) { // Check if valid DB ID
      this.http.patch<any>(`${this.API_URL}/${id}/read`, {}).subscribe({
        error: () => {},
      });
    }
  }

  markAllAsRead(): void {
    this.notifications.update((items) => items.map((n) => ({ ...n, read: true })));

    this.http.patch<any>(`${this.API_URL}/read-all`, {}).subscribe({
      error: () => {},
    });
  }
}
